import { useTranslation } from 'react-i18next';
import { useAsync } from 'react-async';
import { KoobPageHeader } from '@koob/ui';
import { OrganizationScopeEnum, ProductTypeEnum } from '@koob/enums';
import { useApp, useNotifications } from 'hooks';
import { defaultWithBookingsBetween } from 'utils';
import { getBookings, getPartners } from 'api/node/bookings';
import { getHotels } from 'api/node/hotels';
import { getFolders } from 'api/node/folder';
import { getExperiences } from 'api/node/experiences';
import CreateBookingButton from './Partials/CreateBookingButton';

const total = (response) => Number(response?.headers?.xTotalCount ?? 0);

// Each tab counts what its list shows: bookings, hotels, folders,
// experiences or clients.
const useMyBookingsTabs = (enabled) => {
  const { t } = useTranslation('bookings');
  const { currentOrganizationId, currentOrganization } = useApp();
  const path = '/bookings/my-bookings';

  const { data: bookings } = useAsync({
    promiseFn: enabled ? getBookings : undefined,
    filters: {
      scope: OrganizationScopeEnum.DMC,
      withReplacedBookings: false,
      // The group view spans its organizations: the API scopes it already.
      organizationId: currentOrganization?.isGroup
        ? undefined
        : currentOrganizationId,
      product: ProductTypeEnum.HOTEL,
      perPage: 0,
      page: 1,
    },
  });
  const { data: hotels } = useAsync({
    promiseFn: enabled ? getHotels : undefined,
    filters: {
      withBookingsBetween: defaultWithBookingsBetween,
      withBookingsScope: OrganizationScopeEnum.DMC,
      page: 1,
      perPage: 1,
    },
  });
  const { data: folders } = useAsync({
    promiseFn: enabled ? getFolders : undefined,
    filters: {
      withBookingsScope: OrganizationScopeEnum.DMC,
      withBookingsBetween: defaultWithBookingsBetween,
      page: 1,
      perPage: 0,
    },
  });

  return [
    { label: t('dayByDay'), to: `${path}/by-day`, count: total(bookings) },
    { label: t('byHotels'), to: `${path}/by-hotel`, count: total(hotels) },
    {
      label: t('byClientFolders'),
      to: `${path}/by-client-folder`,
      count: total(folders),
    },
  ];
};

const usePartnerBookingsTabs = (enabled) => {
  const { t } = useTranslation('bookings');
  const { currentOrganizationId } = useApp();
  const { notifications } = useNotifications();
  const path = '/bookings/partner-bookings';

  const unread = notifications.filter(
    ({ notificationType, read }) =>
      /^(booking_.+|update_hotel|update_experience)$/.test(notificationType) &&
      !read,
  ).length;

  const { data: bookings } = useAsync({
    promiseFn: enabled ? getBookings : undefined,
    filters: {
      scope: OrganizationScopeEnum.TO,
      withReplacedBookings: false,
      perPage: 0,
      page: 1,
    },
  });
  const { data: hotels } = useAsync({
    promiseFn: enabled ? getHotels : undefined,
    filters: {
      withBookingsBetween: defaultWithBookingsBetween,
      withBookingsScope: OrganizationScopeEnum.TO,
      page: 1,
      perPage: 1,
    },
  });
  const { data: experiences } = useAsync({
    promiseFn: enabled ? getExperiences : undefined,
    filters: {
      withBookingsBetween: defaultWithBookingsBetween,
      withBookingsScope: OrganizationScopeEnum.TO,
      page: 1,
      perPage: 1,
    },
  });
  const { data: partners } = useAsync({
    promiseFn: enabled ? getPartners : undefined,
    filters: {
      scope: OrganizationScopeEnum.TO,
      organizationId: currentOrganizationId,
      withReplacedBookings: false,
      page: 1,
      perPage: 1,
    },
  });

  return [
    { label: t('dayByDay'), to: `${path}/by-day`, count: total(bookings) },
    { label: t('byHotels'), to: `${path}/by-hotel`, count: total(hotels) },
    {
      label: t('byExperience'),
      to: `${path}/by-experience`,
      count: total(experiences),
    },
    {
      label: t('byPartners'),
      to: `${path}/by-partners`,
      count: total(partners),
      badge: unread,
    },
  ];
};

/**
 * The header of the My bookings and Clients' bookings lists: the section's
 * title and its tabs. `partner`: Clients' bookings.
 */
export default function BookingsHeader({ partner = false, subtitle }) {
  const { t } = useTranslation('bookings');
  const { isAllowedTo } = useApp();
  const myTabs = useMyBookingsTabs(!partner);
  const partnerTabs = usePartnerBookingsTabs(partner);

  return (
    <KoobPageHeader
      title={t(partner ? 'partnerBookings' : 'myBookings')}
      subtitle={subtitle}
      actions={
        !partner && isAllowedTo('manageBookings') && <CreateBookingButton />
      }
      tabs={partner ? partnerTabs : myTabs}
    />
  );
}

/** Where a booking's own list lives: a DMC's bookings, or its clients'. */
export const bookingsListPath = (booking) =>
  (booking?.organization?.scope ?? booking?.user?.organization?.scope) ===
  OrganizationScopeEnum.DMC
    ? '/bookings/my-bookings'
    : '/bookings/partner-bookings';

const TAB_LABELS = {
  'by-hotel': 'byHotels',
  'by-client-folder': 'byClientFolders',
  'by-experience': 'byExperience',
  'by-partners': 'byPartners',
};

/**
 * A sub-list's breadcrumbs (one hotel's, folder's, experience's or client's
 * bookings): its section, then the tab it opens from.
 */
export const useBookingsCrumbs = ({ partner = false, tab }) => {
  const { t } = useTranslation('bookings');
  const path = partner ? '/bookings/partner-bookings' : '/bookings/my-bookings';

  return [
    { label: t(partner ? 'partnerBookings' : 'myBookings'), to: path },
    { label: t(TAB_LABELS[tab]), to: `${path}/${tab}` },
  ];
};
